// 自分のポインタ位置と表示ビューを viewer.sync として一定間隔で送信する hook。
// 30Hz だと API Gateway の課金が重いため 10Hz（100ms）に制限する。
// view（pan / zoom）はフォーカスモード追尾用に他クライアントへ即時中継される。

import { useEffect, useRef } from "react";
import { useRealtimeClient } from "../../realtime/realtime-context";
import { useCurrentActor, type CurrentActor } from "../../auth/use-current-actor";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  screenToStage,
} from "../../lib/canvas-coordinates";
import { useCanvasStore } from "./store/canvas-store";
import type { RealtimeClient } from "../../realtime/realtime-client";
import type { Viewer } from "@refinement-canvas/domain";

const BROADCAST_INTERVAL_MS = 100;
const VIEW_BROADCAST_INTERVAL_MS = 100;
// 静止中（pan/zoom していない）でも、後から参加した利用者がフォーカスモードで追尾を
// 開始できるよう、現在 view を低頻度で再送信するハートビート間隔。
// viewer.view は永続化されない一時情報のため、新規参加者は再送が無いと対象の view を取得できない。
const VIEW_HEARTBEAT_INTERVAL_MS = 2000;

export function useCursorBroadcaster(): void {
  const client = useRealtimeClient();
  const actor = useCurrentActor();
  const selectedObjectIds = useCanvasStore((s) => s.selectedObjectIds);
  const view = useCanvasStore((s) => s.view);
  const lastSentRef = useRef<number>(0);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);
  // pan/zoom が変化したかどうかを追跡し、変化していない間は view を送らない。
  const lastSentViewRef = useRef<Viewer["view"] | null>(null);
  // 連続的な pan/zoom 中の view 送信を 100ms に間引くためのタイマー ID。
  const viewFlushTimerRef = useRef<number | undefined>(undefined);
  // 最新の view を ref 経由で参照する（pointermove ハンドラ内で常に最新を取れるようにする）。
  const viewRef = useRef(view);
  viewRef.current = view;
  // 遅延 flush 時に最新の選択状態を参照するための ref。
  // タイマー作成時点の closure に固定された古い selectedObjectIds で上書きするのを防ぐ。
  const selectedObjectIdsRef = useRef(selectedObjectIds);
  selectedObjectIdsRef.current = selectedObjectIds;

  useEffect(() => {
    // 初期表示と選択変更は viewer.sync（カーソル・view なし）として共有する。
    // view を載せるとサーバ側が viewer.snapshot を配信しなくなり、参加者一覧や他者選択枠が
    // 更新されなくなるため、メタデータ同期は必ず view なしで送って snapshot を発火させる。
    // 初期 view は下の throttle effect が初回マウント時に別途送信する。
    sendViewerSync(client, actor, selectedObjectIds);
  }, [actor, client, selectedObjectIds]);

  // 再接続時に presence を再登録する。サーバは cursor / view なしの viewer.sync でのみ
  // viewer.snapshot を broadcast するため、再接続後に plain viewer.sync を送り直して
  // 自分を参加者一覧へ復帰させる（cursor / view ハートビートだけでは snapshot が出ない）。
  useEffect(() => {
    return client.onReopen(() => {
      sendViewerSync(client, actor, selectedObjectIdsRef.current);
    });
  }, [actor, client]);

  // view 変更を 100ms 単位にスロットリングして送信する。
  // 直近送信から 100ms 以上経過していれば即時、未経過なら残り時間後にまとめて送信する。
  // 初回マウント時は lastSentViewRef が null のため、現在 view を 1 度だけ送信する。
  useEffect(() => {
    const last = lastSentViewRef.current;
    const sameAsLast =
      !!last &&
      Math.abs(last.panX - view.panX) < 0.5 &&
      Math.abs(last.panY - view.panY) < 0.5 &&
      Math.abs(last.zoom - view.zoom) < 1e-4;
    if (sameAsLast) return undefined;

    const flush = (): void => {
      viewFlushTimerRef.current = undefined;
      const currentView = viewRef.current;
      // 選択状態は最新値を ref から読む（古い closure 値での上書きを防ぐ）。
      sendViewerSync(client, actor, selectedObjectIdsRef.current, undefined, currentView);
      lastSentViewRef.current = {
        panX: currentView.panX,
        panY: currentView.panY,
        zoom: currentView.zoom,
      };
    };

    if (viewFlushTimerRef.current !== undefined) {
      // 既存タイマー中（短時間に view が再度変わったケース）はタイマーの満了を待つ。
      return undefined;
    }
    viewFlushTimerRef.current = window.setTimeout(flush, VIEW_BROADCAST_INTERVAL_MS);
    return () => {
      // 後続の useEffect で view が再変更されたとき、タイマーは保ったままで次の flush を待つ。
      // unmount 時のみクリアする。
    };
    // selectedObjectIds は ref 経由で参照するため依存に含めない（view 変化時のみ flush を予約する）。
  }, [actor, client, view]);

  // unmount で残タイマーをクリーンアップ。
  useEffect(() => {
    return () => {
      if (viewFlushTimerRef.current !== undefined) {
        window.clearTimeout(viewFlushTimerRef.current);
        viewFlushTimerRef.current = undefined;
      }
    };
  }, []);

  // 静止中の view ハートビート。
  // pan/zoom が止まると上の throttle effect は発火しないため、フォーカス追尾の元データが
  // 新規参加者へ届かなくなる。低頻度（2 秒間隔）で現在 view を再送して追尾開始を可能にする。
  useEffect(() => {
    const id = window.setInterval(() => {
      const currentView = viewRef.current;
      sendViewerSync(client, actor, selectedObjectIdsRef.current, undefined, currentView);
      lastSentViewRef.current = {
        panX: currentView.panX,
        panY: currentView.panY,
        zoom: currentView.zoom,
      };
    }, VIEW_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [actor, client]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent): void => {
      const currentView = viewRef.current;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, currentView);
      const xRatio = clamp01(stagePoint.x / CANVAS_WIDTH_PX);
      const yRatio = clamp01(stagePoint.y / CANVAS_HEIGHT_PX);
      pendingRef.current = { x: xRatio, y: yRatio };
      flushIfDue();
    };

    const flushIfDue = (): void => {
      const now = Date.now();
      if (now - lastSentRef.current < BROADCAST_INTERVAL_MS) return;
      const pending = pendingRef.current;
      if (!pending) return;
      lastSentRef.current = now;
      pendingRef.current = null;
      sendViewerSync(client, actor, selectedObjectIds, {
        xRatio: pending.x,
        yRatio: pending.y,
      });
    };

    window.addEventListener("pointermove", onPointerMove);
    const intervalId = window.setInterval(flushIfDue, BROADCAST_INTERVAL_MS);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.clearInterval(intervalId);
    };
  }, [actor, client, selectedObjectIds]);
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function sendViewerSync(
  client: RealtimeClient,
  actor: CurrentActor,
  selectedObjectIds: readonly string[],
  cursor?: Viewer["cursor"],
  view?: Viewer["view"]
): void {
  client.sendMessage({
    type: "viewer.sync",
    viewer: {
      userId: actor.userId,
      displayName: actor.displayName,
      color: actor.color,
      avatarImageUrl: actor.avatarImageUrl,
      selectedObjectIds,
      ...(cursor ? { cursor } : {}),
      ...(view ? { view } : {}),
    },
  });
}
